import {
    Autocomplete,
    createFilterOptions,
    FormControl,
    Grid,
    ListItemAvatar,
    ListItemText,
    Stack,
    TextField
} from "@mui/material";
import React, {useMemo} from "react";
import {mapSetState, SetState} from "../../util/FormHandlers";
import {Residence, ResidenceModel, useResidenceStore} from "../../api/ResidenceStore";
import {useRemoteValue} from "../../util/RemoteValue";
import {SiteChip} from "../site-chip/SiteChip";
import {badgeOrDefault} from "../../api/WorkSiteMapStore";
import {Opt} from "../../util/Optional";
import {HibobOffice, WorkSite} from "../../api/Model";
import {filterNotNull} from "../../util/ArrayUtil";


function renderOption({key, ...props}: React.HTMLAttributes<HTMLLIElement> & { key: any }, residence: Residence) {
    let name: string;
    let siteId: Opt<WorkSite['id']>;
    let hibobOfficeId: Opt<HibobOffice['id']>;
    switch (residence.type) {
        case "residents":
            name = 'Residents';
            siteId = undefined;
            hibobOfficeId = undefined;
            break;
        case "not-residents":
            siteId = undefined;
            hibobOfficeId = undefined;
            name = 'Not Residents';
            break;
        case "work-site":
            name = residence.workSite.name;
            siteId = residence.workSite.id;
            hibobOfficeId = undefined;
            break;
        case "hibob-office":
            name = residence.hibobOffice.name;
            siteId = residence.hibobOffice.workSiteId;
            hibobOfficeId = residence.hibobOffice.id;
            break;
    }

    return (
        <Stack key={key} component="li" direction="row" spacing={2} justifyItems="stretch" {...props}>
            <ListItemText primary={name} slotProps={{root: {sx: {pl: residence.type === 'hibob-office' ? 2 : undefined}}, primary: {sx: {textWrap: 'nowrap'}}}}/>
            {(siteId || hibobOfficeId) && (
                <ListItemAvatar>
                    <SiteChip siteId={siteId} hibobOfficeId={hibobOfficeId}/>
                </ListItemAvatar>
            )}
        </Stack>
    );
}

function optionLabel(residence: Residence): string {
    switch (residence.type) {
        case "residents":
            return "Residents";
        case "not-residents":
            return "Not Residents";
        case "work-site":
            return badgeOrDefault(residence.workSite);
        case "hibob-office":
            return badgeOrDefault(residence.hibobOffice);
    }
}

function optionEq(a: Residence, b: Residence): boolean {
    return ResidenceModel.serialize(a) === ResidenceModel.serialize(b);
}

const residenceTextFilter = createFilterOptions<Residence>({
    ignoreAccents: true,
    ignoreCase: true,
    stringify: residence => {
        switch (residence.type) {
            case "residents":
            case "not-residents":
                return optionLabel(residence);
            case "work-site":
                return filterNotNull([
                    residence.workSite.emoji,
                    residence.workSite.name,
                    residence.workSite.badge,
                ]).join(' ').toString();
            case "hibob-office":
                return filterNotNull([
                    residence.hibobOffice.name,
                    residence.hibobOffice.badge,
                ]).join(' ').toString();
        }
    },
});

export function ResidenceFilter<Filter extends { residency: Residence | null }>({filter, setFilter}: {
    filter: Filter,
    setFilter: SetState<Filter>,
}) {
    const residencyModel = useResidenceStore().remoteValue;
    const setResidency = useMemo(
        () => mapSetState(setFilter, 'residency'),
        [setFilter]
    );

    const optionsRemoteValue = useMemo(() =>
            residencyModel.map(residency => residency.options),
        [residencyModel],
    );
    const options = useRemoteValue(optionsRemoteValue, []);

    return (
        <Grid size={{lg: 2, md: 3, xs: 12}}>
            <FormControl fullWidth variant="outlined">
                <Autocomplete<Residence>
                    fullWidth
                    id="work-site-select"
                    options={options}
                    value={filter.residency}
                    onChange={(_, value) => setResidency(value)}
                    renderInput={params => <TextField {...params} label="Residence"/>}
                    renderOption={renderOption}
                    filterOptions={residenceTextFilter}
                    getOptionKey={ResidenceModel.serialize}
                    getOptionLabel={optionLabel}
                    isOptionEqualToValue={optionEq}
                    slotProps={{paper: {sx: {width: 'fit-content'}}}}
                />
            </FormControl>
        </Grid>
    );
}
